import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import { SystemLogo } from '@/components/common';

import { useAuth } from '@/hooks/useAuth';
import { useCaptcha } from '@/hooks/useCaptcha';
import {
  fetchCreateWeChatOfficialQrTicket,
  fetchCheckWeChatOfficialQrStatus,
  fetchExchangeWeChatOfficialQrTicket,
  fetchBindWeChatOfficialPhone,
} from '@/service/api/account';
import { useAuthStore } from '@/stores/authStore';

import {
  WeChatLoginModule,
  PhoneLoginModule,
  BindPhoneModule,
  AgreementModal,
} from './modules';

export default function AuthPage() {
  // 登录方式：'wechat' 微信扫码登录 | 'phone' 手机号验证码登录
  const [loginMethod, setLoginMethod] = useState<'wechat' | 'phone'>('wechat');

  // 表单基础状态
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [accountType, setAccountType] = useState<'PERSONAL' | 'ENTERPRISE'>('PERSONAL');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');

  // 微信扫码相关状态
  const [qrTicket, setQrTicket] = useState<Api.Account.WeChatOfficialQrTicketResponse | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrStatusText, setQrStatusText] = useState('正在生成二维码...');
  const [qrExpired, setQrExpired] = useState(false);
  const [qrExchanging, setQrExchanging] = useState(false);

  // 扫码成功但未绑定手机号的状态
  const [needBindPhone, setNeedBindPhone] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  // 协议弹窗与等待执行的操作状态
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'sendCode' | 'submit' | 'bindPhone' | null>(null);

  const { login, status, clearError } = useAuth();
  const { label: captchaLabel, isCounting, loading: captchaLoading, getCaptcha } = useCaptcha();
  const navigate = useNavigate();
  const location = useLocation();

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isFetchingQrRef = useRef(false);
  const isWeChatActiveRef = useRef(false);

  const navigateAfterLogin = () => {
    const state = location.state as { from?: string } | null;
    navigate(state?.from || '/', { replace: true });
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const startWeChatQr = async (overrideAccountType?: 'PERSONAL' | 'ENTERPRISE') => {
    if (isFetchingQrRef.current) return;
    isFetchingQrRef.current = true;

    stopPolling();
    setQrLoading(true);
    setQrExpired(false);
    setQrTicket(null);
    setQrStatusText('正在获取微信二维码...');
    setError('');

    const currentType = overrideAccountType || accountType;

    try {
      const data = await fetchCreateWeChatOfficialQrTicket({
        account_type: currentType,
      });

      if (!isWeChatActiveRef.current) {
        setQrLoading(false);
        return;
      }

      setQrTicket(data);
      setQrLoading(false);
      setQrStatusText('请使用微信扫描二维码并关注公众号');

      // 开启 2 秒轮询
      pollTimerRef.current = setInterval(async () => {
        if (!isWeChatActiveRef.current) {
          stopPolling();
          return;
        }
        try {
          const statusRes = await fetchCheckWeChatOfficialQrStatus(data.ticket_id, {
            poll_token: data.poll_token,
          });

          if (!isWeChatActiveRef.current) {
            stopPolling();
            return;
          }

          setQrStatusText(
            statusRes.status === 'CONFIRMED'
              ? '扫码成功，正在登录...'
              : '请使用微信扫描二维码并关注公众号'
          );

          if (statusRes.status === 'CONFIRMED') {
            stopPolling();
            await handleExchange(data.ticket_id, data.poll_token);
          } else if (statusRes.status === 'EXPIRED') {
            stopPolling();
            setQrExpired(true);
            setQrStatusText('二维码已过期，请刷新重新获取');
          }
        } catch (err: any) {
          const msg = err instanceof Error ? err.message : '查询二维码状态失败';
          if (/expired|invalid/i.test(msg)) {
            stopPolling();
            setQrExpired(true);
            setQrStatusText('二维码已失效，请点击刷新');
          }
        }
      }, 2000);
    } catch (err) {
      if (isWeChatActiveRef.current) {
        setQrLoading(false);
        const msg = err instanceof Error ? err.message : '创建二维码失败';
        setQrStatusText(msg);
        setError(msg);
      }
    } finally {
      isFetchingQrRef.current = false;
    }
  };

  const handleExchange = async (ticketId: string, pollToken: string) => {
    if (qrExchanging) return;
    setQrExchanging(true);
    setError('');

    try {
      const res = await fetchExchangeWeChatOfficialQrTicket(ticketId, {
        poll_token: pollToken,
        device_id: 'pc-web',
      });

      if (res.access_token) {
        setSessionToken(res.access_token);
      }

      if (res.phone_bound !== false) {
        await useAuthStore.getState().loginWithSessionResponse(res);
        navigateAfterLogin();
      } else {
        setNeedBindPhone(true);
        setError('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录兑换失败，请重试');
    } finally {
      setQrExchanging(false);
    }
  };

  // 打开登录界面时，仅当已处于登录状态才自动退出登录
  useEffect(() => {
    if (useAuthStore.getState().status === 'authenticated') {
      useAuthStore.getState().logout();
    }
  }, []);

  // 微信扫码 Tab 激活时触发启动二维码
  useEffect(() => {
    if (loginMethod === 'wechat' && !needBindPhone) {
      isWeChatActiveRef.current = true;
      startWeChatQr();
    } else {
      isWeChatActiveRef.current = false;
      stopPolling();
    }
    return () => {
      isWeChatActiveRef.current = false;
      stopPolling();
    };
  }, [loginMethod, needBindPhone]);

  // 发送短信验证码
  const executeSendCode = async () => {
    setError('');
    clearError();
    try {
      await getCaptcha(phone);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取验证码失败');
    }
  };

  const handleSendCodeClick = async () => {
    if (!agreed) {
      setPendingAction('sendCode');
      setShowAgreementModal(true);
      return;
    }
    await executeSendCode();
  };

  // 手机号快捷登录
  const executeLogin = async () => {
    setError('');
    clearError();

    if (phone.length < 11) {
      setError('请输入正确的手机号');
      return;
    }
    if (code.length !== 6) {
      setError('请输入 6 位验证码');
      return;
    }

    try {
      await login(phone, code, accountType);
      navigateAfterLogin();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : '操作失败，请稍后重试');
    }
  };

  // 微信扫码后绑定手机号提交
  const executeBindPhone = async () => {
    setError('');
    clearError();

    if (phone.length < 11) {
      setError('请输入正确的手机号');
      return;
    }
    if (code.length !== 6) {
      setError('请输入 6 位验证码');
      return;
    }

    try {
      if (sessionToken) {
        localStorage.setItem('access_token', sessionToken);
        localStorage.setItem('token', sessionToken);
      }
      const res = await fetchBindWeChatOfficialPhone({
        phone,
        verification_code: code,
        account_type: accountType,
        agreement_accepted: true,
        device_id: 'pc-web',
      });

      await useAuthStore.getState().loginWithSessionResponse(res);
      navigateAfterLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : '绑定手机号失败，请重试');
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      setPendingAction('submit');
      setShowAgreementModal(true);
      return;
    }
    await executeLogin();
  };

  const handleBindPhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      setPendingAction('bindPhone');
      setShowAgreementModal(true);
      return;
    }
    await executeBindPhone();
  };

  const handleConfirmAgreement = async () => {
    setAgreed(true);
    setShowAgreementModal(false);
    const action = pendingAction;
    setPendingAction(null);

    if (action === 'sendCode') {
      await executeSendCode();
    } else if (action === 'submit') {
      await executeLogin();
    } else if (action === 'bindPhone') {
      await executeBindPhone();
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="pt-28 sm:pt-32 flex items-center justify-center min-h-[calc(100vh-64px)] py-10">
        <div className="w-full max-w-md mx-auto px-4">
          <div className="glass-card rounded-2xl p-6 md:p-8">
            {/* Logo / Title */}
            <div className="text-center mb-6">
              <Link to="/" className="inline-flex items-center mb-5 cursor-pointer">
                <SystemLogo sizeClassName="h-10 md:h-12 xl:h-14 2xl:h-16 3xl:h-20 w-auto" textClassName="text-lg 2xl:text-2xl" />
              </Link>
              <h1 className="font-heading font-bold text-xl text-foreground-950">
                {needBindPhone ? '绑定手机号' : '登录 / 注册'}
              </h1>
              <p className="text-foreground-500 text-xs mt-1">
                {needBindPhone
                  ? '微信授权成功，请绑定手机号完成最终登录'
                  : loginMethod === 'wechat'
                    ? '微信扫码关注公众号一键登录'
                    : '使用手机号快捷登录，未注册账号将自动创建'}
              </p>
            </div>

            {/* 错误提示框 */}
            {error && (
              <div className="mb-5 flex items-center gap-2 text-xs text-accent-500 bg-accent-50 rounded-lg px-3 py-2.5">
                <i className="ri-error-warning-line flex-shrink-0"></i>
                <span className="flex-1">{error}</span>
              </div>
            )}

            {/* 主内容区域 */}
            {needBindPhone ? (
              /* View 3: 独立绑定手机号新界面 */
              <BindPhoneModule
                phone={phone}
                setPhone={setPhone}
                code={code}
                setCode={setCode}
                accountType={accountType}
                setAccountType={setAccountType}
                agreed={agreed}
                setAgreed={setAgreed}
                status={status}
                isCounting={isCounting}
                captchaLoading={captchaLoading}
                captchaLabel={captchaLabel}
                onSendCodeClick={handleSendCodeClick}
                onSubmit={handleBindPhoneSubmit}
                onBackToWeChat={() => {
                  setNeedBindPhone(false);
                  setLoginMethod('wechat');
                  setError('');
                }}
                onAccountTypeChange={() => {
                  setError('');
                  clearError();
                }}
              />
            ) : (
              <>
                {/* View 1: 微信扫码登录模式 */}
                {loginMethod === 'wechat' && (
                  <WeChatLoginModule
                    qrLoading={qrLoading}
                    qrExpired={qrExpired}
                    qrTicket={qrTicket}
                    qrExchanging={qrExchanging}
                    qrStatusText={qrStatusText}
                    accountType={accountType}
                    setAccountType={setAccountType}
                    onAccountTypeChange={(newType) => {
                      setError('');
                      clearError();
                      startWeChatQr(newType);
                    }}
                    onRefreshQr={() => startWeChatQr()}
                  />
                )}

                {/* View 2: 手机号登录模式 */}
                {loginMethod === 'phone' && (
                  <PhoneLoginModule
                    phone={phone}
                    setPhone={setPhone}
                    code={code}
                    setCode={setCode}
                    accountType={accountType}
                    setAccountType={setAccountType}
                    agreed={agreed}
                    setAgreed={setAgreed}
                    status={status}
                    isCounting={isCounting}
                    captchaLoading={captchaLoading}
                    captchaLabel={captchaLabel}
                    onSendCodeClick={handleSendCodeClick}
                    onSubmit={handlePhoneSubmit}
                    onAccountTypeChange={() => {
                      setError('');
                      clearError();
                    }}
                  />
                )}

                {/* Divider & Social Login */}
                <div className="my-6 flex items-center gap-3">
                  <div className="flex-1 h-px bg-background-200"></div>
                  <span className="text-xs text-foreground-400">其他登录方式</span>
                  <div className="flex-1 h-px bg-background-200"></div>
                </div>

                <div className="flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod('wechat');
                      setError('');
                      clearError();
                    }}
                    title="微信扫码登录"
                    className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-all cursor-pointer ${loginMethod === 'wechat'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-600'
                      : 'border-gray-300/60 hover:bg-gray-50 text-foreground-600'
                      }`}
                  >
                    <i className="ri-wechat-line text-xl"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod('phone');
                      setError('');
                      clearError();
                    }}
                    title="手机号登录"
                    className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-all cursor-pointer ${loginMethod === 'phone'
                      ? 'border-primary-500 bg-primary-50 text-primary-600'
                      : 'border-gray-300/60 hover:bg-gray-50 text-foreground-600'
                      }`}
                  >
                    <i className="ri-smartphone-line text-xl"></i>
                  </button>
                </div>
              </>
            )}

            {/* Back to Home */}
            <div className="mt-6 text-center">
              <Link to="/" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* 服务条款及隐私政策弹窗 */}
      <AgreementModal
        show={showAgreementModal}
        onClose={() => {
          setShowAgreementModal(false);
          setPendingAction(null);
        }}
        onConfirm={handleConfirmAgreement}
      />
    </div>
  );
}
